/** Recherche d'un mot ou d'une expression dans le texte d'un livre.
 *  Les paragraphes doivent coller au texte que la page affiche, pour que
 *  l'ancre d'un résultat retrouve la bonne page dans le lecteur.
 */
import { decodeHtmlEntities } from "./libraryPlainText";

export const SEARCH_MIN_LENGTH = 2;
export const SEARCH_RESULT_LIMIT = 300;

const PASSAGE_MAX = 360;
const PASSAGE_BEFORE = 150;
const ANCHOR_LENGTH = 120;
const ANCHOR_MIN = 40;

const INVISIBLE = new Set(["\u00ad", "\u200b", "\u200c", "\u200d", "\u2060", "\ufeff"]);
const SINGLE_QUOTES = new Set(["\u2019", "\u2018", "\u02bc", "\u00b4", "`"]);
const DOUBLE_QUOTES = new Set(["\u201c", "\u201d", "\u201e", "\u00ab", "\u00bb"]);
const DASHES = new Set(["\u2010", "\u2011", "\u2012", "\u2013", "\u2014", "\u2212"]);

const TERMINATORS = new Set([".", "!", "?", "\u2026"]);
const CLOSERS = new Set(["\u00bb", "\u201d", '"', "\u2019", "'", ")", "]", " "]);
const OPENERS = new Set(["\u00ab", "\u201c", '"', "\u2014", "\u2013", "-", "(", "[", " ", "\u2018", "'"]);
const ABBREVIATIONS = new Set([
  "m",
  "mm",
  "mme",
  "mmes",
  "mlle",
  "mlles",
  "dr",
  "me",
  "mgr",
  "pr",
  "st",
  "ste",
  "cf",
  "vol",
  "chap",
]);

const BLOCK_TAGS =
  "p|div|h[1-6]|li|ul|ol|blockquote|section|article|tr|table|header|footer|aside|pre|figure|figcaption|td|th|dd|dt|dl|caption|address|hr";
const BLOCK_OPEN = new RegExp(`<(?:${BLOCK_TAGS})\\b[^>]*>`, "gi");
const BLOCK_CLOSE = new RegExp(`</(?:${BLOCK_TAGS})>`, "gi");

function foldChar(char) {
  const code = char.charCodeAt(0);
  if (code < 128) return code >= 65 && code <= 90 ? String.fromCharCode(code + 32) : char;
  if (INVISIBLE.has(char)) return "";
  if (SINGLE_QUOTES.has(char)) return "'";
  if (DOUBLE_QUOTES.has(char)) return '"';
  if (DASHES.has(char)) return "-";
  if (char === "\u0153" || char === "\u0152") return "oe";
  if (char === "\u00e6" || char === "\u00c6") return "ae";
  return char.normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
}

function foldParts(text, withMap) {
  const parts = [];
  const map = withMap ? [] : null;
  let index = 0;
  for (const char of text) {
    const piece = foldChar(char);
    if (piece) {
      parts.push(piece);
      if (map) for (let step = 0; step < piece.length; step += 1) map.push(index);
    }
    index += char.length;
  }
  return { folded: parts.join(""), map };
}

export function foldSearchText(value) {
  return foldParts(String(value || "").replace(/\s+/g, " ").trim(), false).folded;
}

function htmlParagraphs(html) {
  const marked = String(html || "")
    .replace(/<!--[\s\S]*?-->/g, " ")
    .replace(/<head\b[^>]*>[\s\S]*?<\/head>/gi, " ")
    .replace(/<title\b[^>]*>[\s\S]*?<\/title>/gi, " ")
    .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, " ")
    .replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, " ")
    .replace(/<svg\b[^>]*>[\s\S]*?<\/svg>/gi, " ")
    .replace(/<img\b[^>]*>/gi, " ")
    .replace(/\s+/g, " ")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(BLOCK_OPEN, "\n")
    .replace(BLOCK_CLOSE, "\n")
    .replace(/<[^>]+>/g, "");
  return decodeHtmlEntities(marked)
    .split("\n")
    .map((line) => line.replace(/\s+/g, " ").trim())
    .filter(Boolean);
}

export function buildBookSearchIndex(chapters) {
  return (Array.isArray(chapters) ? chapters : []).map((chapter, chapterIndex) => {
    const lines = [];
    const title = String(chapter?.title || "").replace(/\s+/g, " ").trim();
    if (title) lines.push(title);
    lines.push(...htmlParagraphs(chapter?.html || ""));
    const paragraphs = [];
    let joined = "";
    for (const text of lines) {
      if (joined) joined += " ";
      paragraphs.push({ text, offset: joined.length, folded: foldParts(text, false).folded });
      joined += text;
    }
    return { chapterIndex, joined, paragraphs };
  });
}

function startsSentence(text, at) {
  if (at <= 0) return true;
  if (text[at - 1] !== " ") return false;
  let back = at - 2;
  while (back >= 0 && CLOSERS.has(text[back])) back -= 1;
  if (back < 0 || !TERMINATORS.has(text[back])) return false;
  if (text[back] === ".") {
    let wordStart = back - 1;
    while (wordStart >= 0 && /\p{L}/u.test(text[wordStart])) wordStart -= 1;
    const word = text.slice(wordStart + 1, back);
    if (word.length === 1 && word !== word.toLowerCase()) return false;
    if (ABBREVIATIONS.has(word.toLowerCase())) return false;
  }
  let next = at;
  while (next < text.length && OPENERS.has(text[next])) next += 1;
  if (next >= text.length) return true;
  return /[\p{Lu}\p{N}]/u.test(text[next]);
}

function sentenceStart(text, at) {
  for (let cursor = at; cursor > 0; cursor -= 1) {
    if (text[cursor - 1] === " " && startsSentence(text, cursor)) return cursor;
  }
  return 0;
}

function sentenceEnd(text, from) {
  for (let cursor = from; cursor < text.length; cursor += 1) {
    if (text[cursor] === " " && startsSentence(text, cursor + 1)) return cursor;
  }
  return text.length;
}

function passageWindow(text, start, end, matchStart, matchEnd) {
  if (end - start <= PASSAGE_MAX) return { from: start, to: end };
  let from = Math.max(start, matchStart - PASSAGE_BEFORE);
  let to = Math.min(end, Math.max(from + PASSAGE_MAX, matchEnd));
  if (to - from < PASSAGE_MAX) from = Math.max(start, to - PASSAGE_MAX);
  if (from > start) {
    const space = text.indexOf(" ", from);
    if (space >= 0 && space < matchStart) from = space + 1;
  }
  if (to < end) {
    const space = text.lastIndexOf(" ", to);
    if (space >= matchEnd) to = space;
  }
  return { from, to };
}

function anchorFor(chapter, paragraph, matchStart) {
  const at = paragraph.offset + matchStart;
  const from = Math.max(0, Math.min(at, chapter.joined.length - ANCHOR_MIN));
  return chapter.joined.slice(from, from + ANCHOR_LENGTH);
}

function charLength(text, index) {
  const code = text.codePointAt(index);
  return code != null && code > 0xffff ? 2 : 1;
}

function presentHit(hit) {
  const segments = [];
  const { text } = hit;
  if (hit.from > hit.start) segments.push({ text: "\u2026", hit: false });
  let cursor = hit.from;
  for (const [markStart, markEnd] of hit.marks) {
    if (markStart > cursor) segments.push({ text: text.slice(cursor, markStart), hit: false });
    segments.push({ text: text.slice(markStart, markEnd), hit: true });
    cursor = markEnd;
  }
  if (hit.to > cursor) segments.push({ text: text.slice(cursor, hit.to), hit: false });
  if (hit.to < hit.end) segments.push({ text: "\u2026", hit: false });
  return {
    key: `${hit.chapterIndex}:${hit.paragraphIndex}:${hit.from}`,
    chapterIndex: hit.chapterIndex,
    anchor: hit.anchor,
    segments,
  };
}

export function searchBookIndex(index, query, limit = SEARCH_RESULT_LIMIT) {
  const needle = foldSearchText(query);
  if (needle.length < SEARCH_MIN_LENGTH) return { hits: [], truncated: false };
  const hits = [];
  for (const chapter of Array.isArray(index) ? index : []) {
    for (let paragraphIndex = 0; paragraphIndex < chapter.paragraphs.length; paragraphIndex += 1) {
      const paragraph = chapter.paragraphs[paragraphIndex];
      let at = paragraph.folded.indexOf(needle);
      if (at < 0) continue;
      const { text } = paragraph;
      const { map } = foldParts(text, true);
      let last = null;
      while (at >= 0) {
        const matchStart = map[at];
        const lastChar = map[at + needle.length - 1];
        const matchEnd = lastChar + charLength(text, lastChar);
        if (last && matchStart >= last.from && matchEnd <= last.to) {
          last.marks.push([matchStart, matchEnd]);
        } else {
          if (hits.length >= limit) {
            return { hits: hits.map(presentHit), truncated: true };
          }
          const start = sentenceStart(text, matchStart);
          const end = sentenceEnd(text, matchEnd);
          const { from, to } = passageWindow(text, start, end, matchStart, matchEnd);
          last = {
            chapterIndex: chapter.chapterIndex,
            paragraphIndex,
            text,
            start,
            end,
            from,
            to,
            marks: [[matchStart, matchEnd]],
            anchor: anchorFor(chapter, paragraph, matchStart),
          };
          hits.push(last);
        }
        at = paragraph.folded.indexOf(needle, at + needle.length);
      }
    }
  }
  return { hits: hits.map(presentHit), truncated: false };
}
